<template>
  <SettingsRow
    name="Your numbers"
    description="Reps dial from this shared pool. Numbers rotate automatically so none gets over-used and flagged."
  >
    <div v-if="poolNumbers.length" class="list">
      <div v-for="p in poolNumbers" :key="p.id" class="listRow">
        <span class="num">{{ formatPhone(p.e164) }}</span>
        <StatusChip :tone="p.status_label === 'Healthy' ? 'success' : 'amber'" :label="p.status_label" />
      </div>
    </div>
    <p v-else class="emptyMsg">No numbers yet. Add your first ones below.</p>
  </SettingsRow>

  <SettingsRow
    name="Add numbers"
    description="Pick local numbers in the area codes you call into most. We recommend at least 3 per team. More numbers keep each one healthier for longer."
  >
    <div class="searchRow">
      <input
        v-model="areaCode"
        class="tinput areaInput"
        type="text"
        inputmode="numeric"
        maxlength="3"
        placeholder="415"
        aria-label="Area code"
      />
      <span class="searchHint">Search by area code — we’ll suggest nearby ones if it’s thin.</span>
    </div>

    <div v-if="loading" class="list">
      <div v-for="n in 4" :key="n" class="listRow">
        <Skeleton width="16px" height="16px" radius="4px" />
        <Skeleton width="140px" height="14px" />
        <Skeleton width="90px" height="12px" />
      </div>
    </div>

    <template v-else-if="rows.length">
      <div class="list">
        <label
          v-for="row in visibleRows"
          :key="row.phone_number"
          class="listRow pickRow"
          :class="{ picked: selected.has(row.phone_number) }"
        >
          <input type="checkbox" :checked="selected.has(row.phone_number)" @change="toggle(row.phone_number)" />
          <span class="num">{{ formatPhone(row.phone_number) }}</span>
          <span class="numMeta">
            {{ row.locality || row.region }}<span v-if="row.nearby" class="nearby"> · nearby</span>
          </span>
        </label>
      </div>
      <p v-if="rows.length > MAX_VISIBLE_RESULTS" class="moreHint">
        Showing {{ MAX_VISIBLE_RESULTS }} of {{ rows.length }} — narrow the area code to see others.
      </p>
    </template>

    <p v-else-if="searched" class="emptyMsg">
      {{ emptyMessage }}
    </p>

    <button
      type="button"
      class="btn primary"
      :disabled="selected.size === 0 || adding"
      @click="onAdd"
    >
      {{ adding ? 'Setting up your numbers…' : `Add ${selected.size || ''} ${selected.size === 1 ? 'number' : 'numbers'}`.trim() }}
    </button>
    <p v-if="addError" class="errorMsg">{{ addError }}</p>
  </SettingsRow>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Skeleton from '../../Skeleton.vue'
import StatusChip from './StatusChip.vue'
import SettingsRow from './SettingsRow.vue'
import { useTelephony } from '../../../composables/useTelephony'
import { formatPhone } from '../../../utils/phone'
import { getAvailableNumbers, addTelephonyNumbers, type AvailableNumber } from '../../../../services/dialerApi'

interface Row extends AvailableNumber {
  nearby: boolean
}

// Cap the picker so a dense area code doesn't render as a wall of numbers — reps
// only need a handful to choose from; narrowing the area code surfaces others.
const MAX_VISIBLE_RESULTS = 7

const telephony = useTelephony()

const areaCode = ref('')
const loading = ref(false)
const searched = ref(false)
const rows = ref<Row[]>([])
const suggestionRegions = ref<string[]>([])
const selected = ref<Set<string>>(new Set())
const adding = ref(false)
const addError = ref('')

const poolNumbers = computed(() => telephony.projection.value?.pool.numbers ?? [])

// Only the first N results render; selection + post-add filtering still operate
// on the full `rows` set.
const visibleRows = computed(() => rows.value.slice(0, MAX_VISIBLE_RESULTS))

const emptyMessage = computed(() => {
  if (suggestionRegions.value.length) {
    return `No numbers in ${areaCode.value} right now — try ${suggestionRegions.value.join(' or ')}, which cover the same area.`
  }
  return 'No numbers available right now — try a different area code.'
})

let searchTimer: ReturnType<typeof setTimeout> | null = null

watch(areaCode, () => {
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(runSearch, 400)
})

async function runSearch() {
  loading.value = true
  addError.value = ''
  try {
    const res = await getAvailableNumbers({
      areaCode: areaCode.value.trim() || undefined,
      country: 'US',
    })
    const main: Row[] = (res.numbers || []).map((n) => ({ ...n, nearby: false }))
    const nearby: Row[] = (res.suggestions || []).flatMap((s) =>
      (s.numbers || []).map((n) => ({ ...n, region: n.region || s.region, nearby: true })),
    )
    rows.value = [...main, ...nearby]
    suggestionRegions.value = (res.suggestions || []).map((s) => s.area_code)
  } catch {
    rows.value = []
    suggestionRegions.value = []
  } finally {
    loading.value = false
    searched.value = true
  }
}

function toggle(e164: string) {
  const next = new Set(selected.value)
  if (next.has(e164)) next.delete(e164)
  else next.add(e164)
  selected.value = next
}

async function onAdd() {
  if (!selected.value.size) return
  adding.value = true
  addError.value = ''
  try {
    const res = await addTelephonyNumbers([...selected.value])
    await telephony.refresh()
    const failed = (res.results || []).filter((r) => !r.ok)
    if (failed.length) {
      addError.value = `Couldn’t set up ${failed.length} of ${res.results.length} numbers — try again.`
    }
    selected.value = new Set()
    // Drop the ones we just added from the picker.
    rows.value = rows.value.filter((r) => !res.results.some((x) => x.ok && x.phone_number === r.phone_number))
  } catch {
    addError.value = 'Couldn’t set up those numbers — try again.'
  } finally {
    adding.value = false
  }
}

onMounted(runSearch)
onBeforeUnmount(() => {
  if (searchTimer) clearTimeout(searchTimer)
})
</script>

<style scoped>
.searchRow { display: flex; align-items: center; gap: var(--space-3); flex-wrap: wrap; }
.areaInput { width: 90px; font-family: var(--font-mono); }
.searchHint { font-size: var(--fs-xs); color: var(--color-text-tertiary); }

.list {
  width: 100%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  overflow: hidden;
}
.listRow { display: flex; align-items: center; gap: var(--space-3); padding: 10px 14px; }
.listRow + .listRow { border-top: 1px solid var(--color-border-subtle); }
.listRow > :last-child { margin-left: auto; }
.pickRow { cursor: pointer; transition: background var(--duration-fast) var(--ease-out); }
.pickRow:hover { background: var(--color-bg-subtle); }
.pickRow.picked { background: var(--color-accent-soft); }
.num { font-family: var(--font-mono); font-size: var(--fs-sm); font-weight: var(--fw-medium); font-variant-numeric: tabular-nums; }
.numMeta { font-size: var(--fs-xs); color: var(--color-text-tertiary); }
.nearby { color: var(--color-text-tertiary); font-style: italic; }

.emptyMsg { margin: 0; font-size: var(--fs-sm); line-height: var(--lh-normal); color: var(--color-text-secondary); }
.moreHint { margin: 0; font-size: var(--fs-xs); color: var(--color-text-tertiary); }
.errorMsg { margin: 0; font-size: var(--fs-xs); color: var(--color-danger); }
</style>
